import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, MapPin, Clock as ClockIcon, FileText, Download, UserCheck, Navigation, ChevronDown, Search, Check, ChevronsUpDown } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { toast } from 'sonner';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function normalizeScheduleTime(t) {
  if (t == null || typeof t !== 'string') return '';
  const trimmed = t.trim();
  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return trimmed;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Libellé export (PDF / CSV / Excel) à partir du statut API du pointage. */
function exportScanStatusLabel(status) {
  if (status == null || status === '') return 'Non scanné';
  const s = String(status).toLowerCase();
  if (s === 'valid' || s === 'en_avance' || s === 'a_lheure') return 'Validé';
  if (s === 'en_retard') return 'En retard';
  if (s === 'hors_marge') return 'Hors marge';
  if (s === 'absent') return 'Absent';
  return String(status);
}

function exportScanBadgeMeta(status) {
  const s = String(status || '').toLowerCase();
  const ok = s === 'valid' || s === 'en_avance' || s === 'a_lheure';
  if (ok) return { label: 'VALIDÉ', wide: 28, fill: [34, 197, 94] };
  if (s === 'en_retard') return { label: 'EN RETARD', wide: 34, fill: [234, 88, 12] };
  if (s === 'hors_marge') return { label: 'HORS MARGE', wide: 34, fill: [239, 68, 68] };
  if (s === 'absent') return { label: 'ABSENT', wide: 28, fill: [239, 68, 68] };
  return { label: 'HORS DÉLAI', wide: 34, fill: [239, 68, 68] };
}

export default function AdminPlanningsNew() {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPointage, setSelectedPointage] = useState(null);
  const [modalTab, setModalTab] = useState('validation');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [siteSearchOpen, setSiteSearchOpen] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    // Calculer le début de la semaine courante (lundi)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setWeekStart(monday);
    
    fetchSites();
  }, [navigate]);
  
  useEffect(() => {
    if (selectedSiteId && weekStart) {
      fetchPlanning();
    }
  }, [selectedSiteId, weekStart]);
  
  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/secteurs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data);
      if (response.data.length > 0) {
        setSelectedSiteId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur de chargement des secteurs');
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPlanning = async () => {
    try {
      const token = localStorage.getItem('token');
      const weekStartStr = weekStart.toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/planning/weekly/${selectedSiteId}?week_start=${weekStartStr}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanning(response.data);
    } catch (error) {
      toast.error('Erreur de chargement du planning');
    }
  };
  
  // Fonction fetchMonthPlanning supprimée
  
  const previousWeek = () => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() - 7);
    setWeekStart(newWeekStart);
  };
  
  const nextWeek = () => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() + 7);
    setWeekStart(newWeekStart);
  };
  
  const currentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setWeekStart(monday);
  };
  
  // Fonctions de navigation mois supprimées
  
  const handleOpeningClick = (pointage, opening) => {
    setSelectedPointage({
      pointage: pointage,
      opening: opening
    });
    setDetailsOpen(true);
  };
  
  // Fonction pour vérifier si une ouverture est validée
  const checkOpeningStatus = (opening, pointages, currentDate) => {
    const openingTime = opening.time;
    const marginBefore = opening.margin_before || 30;
    const marginAfter = opening.margin_after || 5;
    
    // Calculer la fenêtre de validité pour cette ouverture
    const openingDateTime = new Date(`${currentDate}T${openingTime}:00`);
    const windowStart = new Date(openingDateTime.getTime() - marginBefore * 60000);
    const windowEnd = new Date(openingDateTime.getTime() + marginAfter * 60000);
    
    // Chercher un pointage correspondant à cette ouverture (par expected_time OU dans la fenêtre temporelle)
    const wantType = opening.type === 'fermeture' ? 'fermeture' : 'ouverture';
    const ot = normalizeScheduleTime(openingTime);
    const byTimeAndType = pointages?.find((p) => {
      if (normalizeScheduleTime(p.expected_time) !== ot) return false;
      return p.scan_type === wantType || p.scan_type == null || p.scan_type === '';
    });
    const matchingPointage =
      byTimeAndType ??
      pointages?.find((p) => normalizeScheduleTime(p.expected_time) === ot) ??
      pointages?.find((p) => {
        if (p.expected_time || !p.timestamp) return false;
        const pointageTime = new Date(p.timestamp);
        return pointageTime >= windowStart && pointageTime <= windowEnd;
      });
    
    if (matchingPointage) {
      // Mapper les statuts du backend vers les nouveaux statuts
      const backendStatus = matchingPointage.status;
      let displayStatus;
      let delayMinutes = 0;
      
      // Calculer le retard/avance
      if (matchingPointage.timestamp) {
        const pointageTime = new Date(matchingPointage.timestamp);
        delayMinutes = Math.round((pointageTime - openingDateTime) / 60000);
      }
      
      if (backendStatus === 'en_avance' || (backendStatus === 'valid' && delayMinutes < 0)) {
        displayStatus = 'en_avance';
      } else if (backendStatus === 'a_lheure' || backendStatus === 'valid') {
        displayStatus = 'a_lheure';
      } else if (backendStatus === 'en_retard' || backendStatus === 'hors_marge') {
        displayStatus = 'en_retard';
      } else {
        displayStatus = 'absent';
      }
      
      return { 
        status: displayStatus, 
        pointage: matchingPointage,
        delayMinutes: delayMinutes
      };
    }
    
    // Si pas de pointage, vérifier si l'ouverture est passée ou future
    const now = new Date();
    
    if (now > windowEnd) {
      // L'ouverture est passée et non pointée = absent
      return { status: 'absent', pointage: null, delayMinutes: 0 };
    } else {
      // L'ouverture est future ou en cours = en attente
      return { status: 'en_attente', pointage: null, delayMinutes: 0 };
    }
  };
  
  // Fonction pour render une ouverture/fermeture
  const renderOpening = (opening, pointages, currentDate) => {
    const { status, pointage, delayMinutes } = checkOpeningStatus(opening, pointages, currentDate);
    
    const isOpening = opening.type === 'ouverture' || !opening.type;
    const typeIcon = isOpening ? '🔓' : '🔒';
    const typeLabel = isOpening ? 'Ouverture' : 'Fermeture';
    
    // Définir les couleurs et textes selon le statut
    let badgeColor, badgeText, badgeIcon;
    
    if (status === 'en_avance') {
      badgeColor = 'bg-green-500 text-white';
      badgeText = `+${Math.abs(delayMinutes)} min`;
      badgeIcon = '✓';
    } else if (status === 'a_lheure') {
      badgeColor = 'bg-green-500 text-white';
      badgeText = 'À l\'heure';
      badgeIcon = '✓';
    } else if (status === 'en_retard') {
      badgeColor = 'bg-orange-500 text-white';
      badgeText = `-${Math.abs(delayMinutes)} min`;
      badgeIcon = '⏰';
    } else if (status === 'absent') {
      badgeColor = 'bg-red-500 text-white';
      badgeText = 'Absent';
      badgeIcon = '✗';
    } else {
      // en_attente
      badgeColor = 'bg-gray-400 text-white';
      badgeText = 'En attente';
      badgeIcon = '⏳';
    }
    
    const isDark = document.documentElement.classList.contains('dark');
    
    return (
      <div 
        className="mb-3 rounded-lg transition-all cursor-pointer p-3"
        style={{
          background: isDark ? '#374151' : '#f3f4f6',
          border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`
        }}
        onClick={() => handleOpeningClick(pointage, opening)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">{typeIcon}</span>
            <span className="font-bold text-lg text-gray-900 dark:text-white">{opening.time}</span>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badgeColor}`}>
            {badgeText}
          </span>
        </div>
        
        <p className="text-xs mb-1" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{typeLabel}</p>
        {opening.description && (
          <p className="text-sm mb-2" style={{ color: isDark ? '#d1d5db' : '#6b7280' }}>{opening.description}</p>
        )}
        
        {pointage && (
          <div className="text-xs" style={{ color: isDark ? '#e5e7eb' : '#374151' }}>
            Scanné à {new Date(pointage.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} 
            <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}> • {pointage.prenom} {pointage.nom}</span>
          </div>
        )}
      </div>
    );
  };
  
  // Helper function to load logo
  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  // Export functions
  const exportToPDF = async (data) => {
    const siteName = sites.find(s => s.id === selectedSiteId)?.nom || 'Site';
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Colors
    const primaryColor = [91, 164, 207]; // #5BA4CF
    const darkText = [30, 41, 59]; // slate-800
    const lightGray = [248, 250, 252]; // slate-50
    const mediumGray = [100, 116, 139]; // slate-500
    const successGreen = [34, 197, 94]; // green-500
    const errorRed = [239, 68, 68]; // red-500
    
    const typeLabel = data.opening.type === 'fermeture' ? 'Fermeture' : 'Ouverture';
    const typeEmoji = data.opening.type === 'fermeture' ? '🔒' : '🔓';
    
    // ===== HEADER SECTION =====
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Add logo
    try {
      const logoBase64 = await loadImageAsBase64('/logo-myopenclose.png');
      doc.addImage(logoBase64, 'PNG', 12, 5, 50, 35);
    } catch (e) {
      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('My Open Close', 15, 28);
    }
    
    // Title on the right
    doc.setTextColor(...darkText);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`DÉTAIL ${typeLabel.toUpperCase()}`, pageWidth - 15, 20, { align: 'right' });
    
    // Subtitle - Site name
    doc.setTextColor(...primaryColor);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(siteName, pageWidth - 15, 30, { align: 'right' });
    
    // Header bottom line
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.8);
    doc.line(15, 45, pageWidth - 15, 45);
    
    // ===== HORAIRES INFO CARD =====
    let y = 55;
    
    // Get scan time if available
    const scanTime = data.pointage 
      ? new Date(data.pointage.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : null;
    
    // Main time card with two columns
    doc.setFillColor(...lightGray);
    doc.roundedRect(15, y, pageWidth - 30, 40, 3, 3, 'F');
    
    // Left column: Horaire prévue
    doc.setTextColor(...mediumGray);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('HORAIRE PRÉVUE', 30, y + 12);
    doc.setTextColor(...primaryColor);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text(data.opening.time, 30, y + 32);
    
    // Vertical separator
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(pageWidth / 2, y + 8, pageWidth / 2, y + 32);
    
    // Right column: Horaire exacte
    doc.setTextColor(...mediumGray);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('HORAIRE EXACTE', pageWidth / 2 + 20, y + 12);
    
    if (scanTime) {
      doc.setTextColor(...darkText);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(scanTime, pageWidth / 2 + 20, y + 32);
    } else {
      doc.setTextColor(...mediumGray);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'italic');
      doc.text('Non scanné', pageWidth / 2 + 20, y + 28);
    }
    
    y += 48;
    
    // Description if exists
    if (data.opening.description) {
      doc.setFillColor(255, 251, 235); // amber-50
      doc.setDrawColor(251, 191, 36); // amber-400
      doc.setLineWidth(0.3);
      doc.roundedRect(15, y, pageWidth - 30, 16, 2, 2, 'FD');
      doc.setTextColor(146, 64, 14); // amber-800
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text(`Description: ${data.opening.description}`, 22, y + 10);
      y += 22;
    }
    
    // ===== VALIDATION SECTION =====
    y += 5;
    doc.setFillColor(...primaryColor);
    doc.roundedRect(15, y, pageWidth - 30, 12, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('VALIDATION DU SCAN', 22, y + 8);
    y += 18;
    
    if (data.pointage) {
      const scanDate = new Date(data.pointage.timestamp);
      const formattedDate = scanDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const formattedTime = scanDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const badge = exportScanBadgeMeta(data.pointage.status);
      doc.setFillColor(...badge.fill);
      doc.roundedRect(15, y, badge.wide, 10, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(badge.label, 15 + badge.wide / 2, y + 7, { align: 'center' });
      
      y += 18;
      
      // Info rows with alternating colors
      const infoRows = [
        { label: 'Employé', value: `${data.pointage.prenom} ${data.pointage.nom}` },
        { label: 'Date', value: formattedDate },
        { label: 'Heure exacte', value: formattedTime },
      ];
      
      if (data.pointage.gps_lat && data.pointage.gps_lng) {
        infoRows.push({ 
          label: 'Position GPS', 
          value: `${data.pointage.gps_lat.toFixed(6)}, ${data.pointage.gps_lng.toFixed(6)}` 
        });
      }
      
      infoRows.forEach((row, idx) => {
        if (idx % 2 === 0) {
          doc.setFillColor(...lightGray);
          doc.rect(15, y - 3, pageWidth - 30, 12, 'F');
        }
        
        doc.setTextColor(...mediumGray);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(row.label.toUpperCase(), 22, y + 4);
        
        doc.setTextColor(...darkText);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(row.value, 70, y + 4);
        
        y += 12;
      });
      
    } else {
      // No scan
      doc.setFillColor(...lightGray);
      doc.roundedRect(15, y, pageWidth - 30, 25, 3, 3, 'F');
      doc.setTextColor(...mediumGray);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'italic');
      doc.text('Aucun scan effectué pour cette ' + typeLabel.toLowerCase(), pageWidth / 2, y + 15, { align: 'center' });
      y += 30;
    }
    
    // ===== FOOTER =====
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.3);
    doc.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
    
    doc.setTextColor(...mediumGray);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 15, pageHeight - 12);
    doc.text('My Open Close', pageWidth - 15, pageHeight - 12, { align: 'right' });
    
    doc.save(`${typeLabel.toLowerCase()}_${siteName.replace(/[^a-z0-9]/gi, '_')}_${data.opening.time.replace(':', 'h')}.pdf`);
    toast.success('PDF exporté avec succès');
  };
  
  const exportToCSV = (data) => {
    const siteName = sites.find(s => s.id === selectedSiteId)?.nom || 'Site';
    const typeLabel = data.opening.type === 'fermeture' ? 'Fermeture' : 'Ouverture';
    
    let csvContent = 'Site;Type;Heure Prévue;Description;Employé;Date Scan;Heure Scan;Statut;Latitude;Longitude\n';
    
    if (data.pointage) {
      const scanDate = new Date(data.pointage.timestamp);
      csvContent += `"${siteName}";"${typeLabel}";"${data.opening.time}";"${data.opening.description || ''}";"${data.pointage.prenom} ${data.pointage.nom}";"${scanDate.toLocaleDateString('fr-FR')}";"${scanDate.toLocaleTimeString('fr-FR')}";"${exportScanStatusLabel(data.pointage.status)}";"${data.pointage.gps_lat || ''}";"${data.pointage.gps_lng || ''}"`;
    } else {
      csvContent += `"${siteName}";"${typeLabel}";"${data.opening.time}";"${data.opening.description || ''}";"";"";"";"";"";""`;
    }
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `scan_${siteName.replace(/[^a-z0-9]/gi, '_')}_${data.opening.time.replace(':', 'h')}.csv`;
    link.click();
    toast.success('CSV exporté avec succès');
  };
  
  const exportToExcel = (data) => {
    const siteName = sites.find(s => s.id === selectedSiteId)?.nom || 'Site';
    const typeLabel = data.opening.type === 'fermeture' ? 'Fermeture' : 'Ouverture';
    
    const worksheetData = [
      ['RAPPORT DE SCAN', '', '', '', '', ''],
      ['Site:', siteName, '', '', '', ''],
      ['', '', '', '', '', ''],
      ['Type', 'Heure Prévue', 'Description', '', '', ''],
      [typeLabel, data.opening.time, data.opening.description || '', '', '', ''],
      ['', '', '', '', '', ''],
    ];
    
    if (data.pointage) {
      const scanDate = new Date(data.pointage.timestamp);
      worksheetData.push(
        ['INFORMATIONS DU SCAN', '', '', '', '', ''],
        ['Employé', 'Date', 'Heure exacte', 'Statut', 'Latitude', 'Longitude'],
        [
          `${data.pointage.prenom} ${data.pointage.nom}`,
          scanDate.toLocaleDateString('fr-FR'),
          scanDate.toLocaleTimeString('fr-FR'),
          exportScanStatusLabel(data.pointage.status),
          data.pointage.gps_lat || '',
          data.pointage.gps_lng || ''
        ]
      );
    } else {
      worksheetData.push(['Aucun scan effectué', '', '', '', '', '']);
    }
    
    worksheetData.push(
      ['', '', '', '', '', ''],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, '', '', '', '', '']
    );
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Style columns width
    worksheet['!cols'] = [
      { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 15 }
    ];
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Scan');
    
    XLSX.writeFile(workbook, `scan_${siteName.replace(/[^a-z0-9]/gi, '_')}_${data.opening.time.replace(':', 'h')}.xlsx`);
    toast.success('Excel exporté avec succès');
  };
  
  // Fonction pour calculer les segments colorés d'un créneau (OLD - à supprimer si non utilisé)
  const calculateSlotSegments = (slot, pointages, currentDate) => {
    const segments = [];
    
    const slotStart = new Date(`${currentDate}T${slot.start}:00`);
    const slotEnd = new Date(`${currentDate}T${slot.end}:00`);
    const now = new Date();
    
    // Filtrer les pointages d'arrivée et départ pour ce créneau
    const arriveePointages = pointages.filter(p => {
      const pTime = new Date(p.timestamp);
      return p.type === 'arrivee' && pTime >= slotStart && pTime <= slotEnd;
    });
    
    const departPointages = pointages.filter(p => {
      const pTime = new Date(p.timestamp);
      return p.type === 'depart';
    });
    
    if (arriveePointages.length === 0) {
      // Pas de pointage arrivée
      if (now > slotEnd) {
        // Créneau passé sans pointage = absence totale (rouge)
        segments.push({
          start: slotStart,
          end: slotEnd,
          color: 'bg-red-500',
          label: 'Absence'
        });
      } else if (now > slotStart) {
        // Créneau en cours, déjà en retard
        segments.push({
          start: slotStart,
          end: now,
          color: 'bg-red-500',
          label: 'Retard'
        });
        segments.push({
          start: now,
          end: slotEnd,
          color: 'bg-zinc-700',
          label: 'En attente'
        });
      } else {
        // Créneau futur
        segments.push({
          start: slotStart,
          end: slotEnd,
          color: 'bg-zinc-700',
          label: 'Prévu'
        });
      }
    } else {
      // Il y a au moins un pointage arrivée
      const firstArrivee = arriveePointages.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      )[0];
      
      const arriveeTime = new Date(firstArrivee.timestamp);
      
      // Vérifier s'il y a un départ anticipé
      const departAnticipe = departPointages.find(d => {
        const departTime = new Date(d.timestamp);
        return departTime < slotEnd && d.status === 'invalid';
      });
      
      if (arriveeTime > slotStart) {
        // Retard à l'arrivée
        segments.push({
          start: slotStart,
          end: arriveeTime,
          color: 'bg-red-500',
          label: `Retard ${Math.round((arriveeTime - slotStart) / 60000)} min`
        });
        
        if (departAnticipe) {
          const departTime = new Date(departAnticipe.timestamp);
          segments.push({
            start: arriveeTime,
            end: departTime,
            color: 'bg-green-500',
            label: 'Présent'
          });
          segments.push({
            start: departTime,
            end: slotEnd,
            color: 'bg-red-500',
            label: 'Départ anticipé'
          });
        } else {
          segments.push({
            start: arriveeTime,
            end: slotEnd,
            color: 'bg-green-500',
            label: 'Présent'
          });
        }
      } else {
        // À l'heure
        if (departAnticipe) {
          const departTime = new Date(departAnticipe.timestamp);
          segments.push({
            start: slotStart,
            end: departTime,
            color: 'bg-green-500',
            label: 'Présent'
          });
          segments.push({
            start: departTime,
            end: slotEnd,
            color: 'bg-red-500',
            label: 'Départ anticipé'
          });
        } else {
          segments.push({
            start: slotStart,
            end: slotEnd,
            color: 'bg-green-500',
            label: 'À l\'heure'
          });
        }
      }
    }
    
    return segments;
  };
  
  const renderTimeSlot = (slot, pointages, currentDate) => {
    const segments = calculateSlotSegments(slot, pointages, currentDate);
    const totalDuration = new Date(`${currentDate}T${slot.end}:00`) - new Date(`${currentDate}T${slot.start}:00`);
    const hasPointages = pointages && pointages.length > 0;
    
    return (
      <div 
        className={`mb-2 bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 ${hasPointages ? 'cursor-pointer hover:border-[#5BA4CF] transition-all' : ''}`}
        onClick={() => hasPointages && handleSlotClick(pointages, slot)}
      >
        <div className="p-2 bg-zinc-800 border-b border-zinc-700">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold">{slot.start} - {slot.end}</span>
            {slot.description && (
              <span className="text-zinc-400">{slot.description}</span>
            )}
          </div>
        </div>
        
        <div className="h-12 flex">
          {segments.map((segment, idx) => {
            const segmentDuration = segment.end - segment.start;
            const widthPercent = (segmentDuration / totalDuration) * 100;
            
            return (
              <div
                key={idx}
                className={`${segment.color} flex items-center justify-center text-xs font-semibold text-white`}
                style={{ width: `${widthPercent}%` }}
                title={segment.label}
              >
                {widthPercent > 15 && <span className="truncate px-1">{segment.label}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5BA4CF]"></div>
        </div>
      </AdminLayout>
    );
  }
  
  if (sites.length === 0) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="glass-card rounded-xl p-12 text-center">
            <CalendarIcon className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">Aucun site configuré</p>
            <Button onClick={() => navigate('/admin/sites')} className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white">
              Créer un site
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Planning</h1>
          <p className="text-gray-600 dark:text-zinc-400 mt-2">Visualisation avec suivi temps réel</p>
        </div>
        
        {/* Sélection du secteur avec dropdown recherchable */}
        <div className="glass-card rounded-xl p-4 mb-6 shadow-md">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#5BA4CF]" />
              <span className="font-medium text-gray-700 dark:text-zinc-300">Secteur :</span>
            </div>
            
            <div className="flex-1">
              <Popover open={siteSearchOpen} onOpenChange={setSiteSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={siteSearchOpen}
                    className="w-full justify-between h-11 px-4 font-medium"
                    style={{
                      background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
                      borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb',
                      color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#1f2937'
                    }}
                    data-testid="site-selector-trigger"
                  >
                    {selectedSiteId
                      ? sites.find((site) => site.id === selectedSiteId)?.nom || "Sélectionner un secteur"
                      : "Sélectionner un secteur"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command
                    className="rounded-lg"
                    style={{
                      background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
                    }}
                  >
                    <CommandInput
                      placeholder="Rechercher un secteur..."
                      className="h-10"
                      data-testid="site-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>Aucun secteur trouvé.</CommandEmpty>
                      <CommandGroup>
                        {sites.map((site) => (
                          <CommandItem
                            key={site.id}
                            value={site.nom}
                            onSelect={() => {
                              setSelectedSiteId(site.id);
                              setSiteSearchOpen(false);
                            }}
                            className="cursor-pointer"
                            style={{
                              color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#1f2937'
                            }}
                            data-testid={`site-option-${site.id}`}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                selectedSiteId === site.id ? "opacity-100 text-[#5BA4CF]" : "opacity-0"
                              }`}
                            />
                            {site.nom}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="glass-card rounded-xl p-4 mb-6 shadow-md">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={previousWeek}
              className="text-gray-900 dark:text-white"
              style={{
                borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb',
                background: 'transparent'
              }}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Semaine précédente
            </Button>
            
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Semaine du {weekStart && weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={currentWeek}
                className="text-[#5BA4CF] hover:text-[#4A93BE] mt-1"
              >
                Aujourd'hui
              </Button>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={nextWeek}
              className="text-gray-900 dark:text-white"
              style={{
                borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb',
                background: 'transparent'
              }}
            >
              Semaine suivante
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Calendrier hebdomadaire */}
        {planning && planning.days && (
          <div className="glass-card rounded-xl p-6">
            <div className="grid grid-cols-7 gap-4">
              {planning.days.map((day, index) => {
                const dayDate = new Date(day.date);
                const isToday = dayDate.toDateString() === new Date().toDateString();
                const daySchedule = day.schedule;
                // Obtenir le vrai jour de la semaine (0 = dimanche, 1 = lundi, etc.)
                const realDayIndex = dayDate.getDay();
                // Convertir pour avoir lundi = 0
                const adjustedDayIndex = realDayIndex === 0 ? 6 : realDayIndex - 1;
                
                return (
                  <motion.div
                    key={day.date}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`rounded-xl overflow-hidden shadow-lg ${
                      isToday ? 'ring-2 ring-[#5BA4CF] ring-offset-2 ring-offset-zinc-950' : ''
                    }`}
                  >
                    <div className={`p-4 text-center ${
                      isToday 
                        ? 'bg-gradient-to-br from-[#5BA4CF] to-[#4A93BE]' 
                        : ''
                    }`}
                    style={!isToday ? {
                      background: document.documentElement.classList.contains('dark') 
                        ? 'linear-gradient(to bottom right, #374151, #1f2937)' 
                        : 'linear-gradient(to bottom right, #e5e7eb, #d1d5db)'
                    } : {}}>
                      <p className="text-xs font-bold uppercase tracking-wider text-white dark:text-zinc-200 text-gray-700">
                        {DAYS_FR[adjustedDayIndex]}
                      </p>
                      <p className="text-2xl font-bold mt-1 text-white dark:text-white text-gray-900">{dayDate.getDate()}</p>
                    </div>
                    
                    <div className="p-3 backdrop-blur-sm min-h-[200px]" style={{
                      background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff'
                    }}>
                      {daySchedule && daySchedule.closed ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="text-3xl mb-2">🔒</div>
                            <p className="text-sm text-zinc-500 font-medium">Fermé</p>
                          </div>
                        </div>
                      ) : daySchedule && daySchedule.openings && daySchedule.openings.length > 0 ? (
                        daySchedule.openings.map((opening, openIdx) => (
                          <div key={openIdx}>
                            {renderOpening(opening, day.pointages || [], day.date)}
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="text-3xl mb-2">📅</div>
                            <p className="text-xs text-zinc-600 font-medium">Aucun créneau</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Légende */}
        <div className="glass-card rounded-xl p-6 mt-6 shadow-md">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Légende</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-lg shadow-md flex items-center justify-center text-white text-sm">✓</div>
              <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">À l'heure / En avance</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-orange-500 rounded-lg shadow-md flex items-center justify-center text-white text-sm">⏰</div>
              <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">En retard</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-500 rounded-lg shadow-md flex items-center justify-center text-white text-sm">✗</div>
              <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">Absent</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-400 rounded-lg shadow-md flex items-center justify-center text-white text-sm">⏳</div>
              <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">En attente</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-zinc-400 dark:bg-zinc-700 rounded-lg flex items-center justify-center text-white text-sm">🔒</div>
              <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">Fermé</span>
            </div>
          </div>
        </div>
        
        {/* Modal Détails Pointage */}
        <AnimatePresence>
          {detailsOpen && selectedPointage && (
            <Dialog open={detailsOpen} onOpenChange={(open) => {
              setDetailsOpen(open);
              if (!open) {
                setModalTab('validation');
                setExportMenuOpen(false);
              }
            }}>
              <DialogContent className="bg-white dark:bg-[#18181B] border-gray-200 dark:border-zinc-800 text-gray-900 dark:text-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <ClockIcon className="h-6 w-6 text-[#5BA4CF]" />
                    Détails de {selectedPointage.opening.type === 'fermeture' ? 'la Fermeture' : "l'Ouverture"}
                  </DialogTitle>
                </DialogHeader>
                
                {/* Header fixe - Horaire, Marges, Description */}
                <div className="flex-shrink-0 border-b pb-4 mb-4" style={{ borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb' }}>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center p-3 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                      <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Heure {selectedPointage.opening.type === 'fermeture' ? 'fermeture' : 'ouverture'}</p>
                      <p className="text-2xl font-bold text-[#5BA4CF]">{selectedPointage.opening.time}</p>
                    </div>
                    <div className="text-center p-3 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                      <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Marge avant</p>
                      <p className="text-xl font-semibold text-gray-700 dark:text-zinc-300">{selectedPointage.opening.margin_before || 30} min</p>
                    </div>
                    <div className="text-center p-3 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                      <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Marge après</p>
                      <p className="text-xl font-semibold text-gray-700 dark:text-zinc-300">{selectedPointage.opening.margin_after || 5} min</p>
                    </div>
                  </div>
                  {selectedPointage.opening.description && (
                    <div className="p-3 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#f8f9fa' }}>
                      <p className="text-sm text-gray-700 dark:text-zinc-300">
                        <FileText className="inline h-4 w-4 mr-2 text-[#5BA4CF]" />
                        {selectedPointage.opening.description}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Onglets */}
                <div className="flex-shrink-0 flex gap-2 mb-4">
                  <button
                    onClick={() => setModalTab('validation')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                      modalTab === 'validation'
                        ? 'bg-[#5BA4CF] text-white'
                        : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                    }`}
                  >
                    <UserCheck className="h-4 w-4" />
                    Validation du scan
                  </button>
                  <button
                    onClick={() => setModalTab('location')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                      modalTab === 'location'
                        ? 'bg-[#5BA4CF] text-white'
                        : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                    }`}
                  >
                    <Navigation className="h-4 w-4" />
                    Emplacement
                  </button>
                </div>
                
                {/* Contenu des onglets */}
                <div className="flex-1 overflow-y-auto">
                  {/* Onglet Validation */}
                  {modalTab === 'validation' && (
                    <div className="space-y-4">
                      {selectedPointage.pointage ? (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                              <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Employé</p>
                              <p className="font-bold text-lg text-gray-900 dark:text-white">
                                {selectedPointage.pointage.prenom} {selectedPointage.pointage.nom}
                              </p>
                            </div>
                            <div className="p-4 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                              <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Statut</p>
                              <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold ${
                                selectedPointage.pointage.status === 'en_avance' || selectedPointage.pointage.status === 'a_lheure' || selectedPointage.pointage.status === 'valid'
                                  ? 'bg-green-500 text-white' 
                                  : selectedPointage.pointage.status === 'en_retard' || selectedPointage.pointage.status === 'hors_marge'
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-red-500 text-white'
                              }`}>
                                {selectedPointage.pointage.status === 'en_avance' ? 'En avance' 
                                  : selectedPointage.pointage.status === 'a_lheure' || selectedPointage.pointage.status === 'valid' ? 'À l\'heure' 
                                  : selectedPointage.pointage.status === 'en_retard' || selectedPointage.pointage.status === 'hors_marge' ? 'En retard'
                                  : 'Absent'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="p-4 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                            <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Heure de scan exacte</p>
                            <p className="font-bold text-2xl text-gray-900 dark:text-white">
                              {new Date(selectedPointage.pointage.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                              {new Date(selectedPointage.pointage.timestamp).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                          
                        </>
                      ) : (
                        <div className="p-8 rounded-lg text-center" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                          <UserCheck className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p className="text-gray-600 dark:text-zinc-400">
                            Aucun scan effectué pour cette {selectedPointage.opening.type === 'fermeture' ? 'fermeture' : 'ouverture'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Onglet Emplacement */}
                  {modalTab === 'location' && (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                        <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Site / Secteur</p>
                        <p className="font-bold text-lg text-gray-900 dark:text-white flex items-center">
                          <MapPin className="h-5 w-5 mr-2 text-[#5BA4CF]" />
                          {sites.find(s => s.id === selectedSiteId)?.nom || 'Site inconnu'}
                        </p>
                      </div>
                      
                      {selectedPointage.pointage?.gps_lat && selectedPointage.pointage?.gps_lng ? (
                        <>
                          <div className="p-4 rounded-lg" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                            <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Coordonnées GPS</p>
                            <p className="font-mono text-sm text-gray-700 dark:text-zinc-300">
                              Lat: {selectedPointage.pointage.gps_lat.toFixed(6)}<br />
                              Lng: {selectedPointage.pointage.gps_lng.toFixed(6)}
                            </p>
                          </div>
                          <div className="h-64 rounded-lg overflow-hidden border" style={{ borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb' }}>
                            <MapContainer 
                              center={[selectedPointage.pointage.gps_lat, selectedPointage.pointage.gps_lng]} 
                              zoom={15} 
                              style={{ height: '100%', width: '100%' }}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              <Marker position={[selectedPointage.pointage.gps_lat, selectedPointage.pointage.gps_lng]}>
                                <Popup>
                                  Position du scan<br />
                                  {selectedPointage.pointage.prenom} {selectedPointage.pointage.nom}
                                </Popup>
                              </Marker>
                            </MapContainer>
                          </div>
                        </>
                      ) : (
                        <div className="p-8 rounded-lg text-center" style={{ background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                          <Navigation className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p className="text-gray-600 dark:text-zinc-400">
                            Position GPS non disponible
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Footer avec boutons */}
                <div className="flex-shrink-0 flex justify-between items-center mt-4 pt-4 border-t" style={{ borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb' }}>
                  {/* Bouton Export */}
                  <div className="relative">
                    <Button
                      onClick={() => setExportMenuOpen(!exportMenuOpen)}
                      className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Exporter
                    </Button>
                    
                    {exportMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
                        <div 
                          className="absolute bottom-full left-0 mb-2 w-40 rounded-lg shadow-lg border overflow-hidden z-50"
                          style={{ 
                            background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
                            borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'
                          }}
                        >
                          <button
                            onClick={() => {
                              exportToPDF(selectedPointage);
                              setExportMenuOpen(false);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-900 dark:text-white"
                          >
                            📄 PDF
                          </button>
                          <button
                            onClick={() => {
                              exportToCSV(selectedPointage);
                              setExportMenuOpen(false);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-900 dark:text-white"
                          >
                            📊 CSV
                          </button>
                          <button
                            onClick={() => {
                              exportToExcel(selectedPointage);
                              setExportMenuOpen(false);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-900 dark:text-white"
                          >
                            📗 Excel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => setDetailsOpen(false)}
                    className="bg-gray-200 dark:bg-zinc-700 hover:bg-gray-300 dark:hover:bg-zinc-600 text-gray-900 dark:text-white"
                  >
                    Fermer
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
}
